/**
 * Laptop Product Detail Page - Interactivity
 * Handles variant selection, image gallery, price updates, and share functionality
 */
(function() {
  'use strict';

  // ==========================================
  // STATE
  // ==========================================
  let productVariants = [];
  let currentVariant = null;
  let optionsMap = {}; // { "CPU": 1, "RAM": 2, "SSD": 3 }
  let restockDates = {}; // { variantId: "dd.mm.yyyy" }
  let deliveryWeeks = {}; // { variantId: "4-6" }
  let ctoFlags = {}; // { variantId: true }
  let geizhalsData = null; // { variantId: { url, price } }
  let availabilityData = null; // { variantId: "store"|"delivery"|"unavailable" }
  let inventoryData = null; // { variantId: { quantity, managed, policy } }

  // ==========================================
  // INITIALIZATION
  // ==========================================
  function init() {
    loadProductVariants();
    loadOptionsMap();
    loadRestockDates();
    loadDeliveryWeeks();
    loadCtoFlags();
    loadGeizhalsData();
    loadAvailabilityData();
    loadInventoryData();
    initVariantSelectors();
    initImageGallery();
    initShareButton();
    initUpsellForm();

    // Initial badges update for the selected variant
    if (currentVariant) {
      updateAutoBadges(currentVariant);
      updateGeizhalsComparison(currentVariant);
      updateAvailability(currentVariant);
      updateLowStock(currentVariant);
      updateDeliveryNotice(currentVariant);
      updateCtoWarning(currentVariant);
      updateSku(currentVariant);
    }
  }

  function loadProductVariants() {
    const variantsScript = document.querySelector('[id^="product-variants-json-"]');
    if (variantsScript) {
      try {
        productVariants = JSON.parse(variantsScript.textContent);
        // Set initial variant
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        if (variantId) {
          currentVariant = productVariants.find(v => v.id == variantId);
        }
        if (!currentVariant && productVariants.length > 0) {
          currentVariant = productVariants.find(v => v.available) || productVariants[0];
        }
      } catch (e) {
        console.error('Error parsing product variants:', e);
      }
    }
  }

  function loadOptionsMap() {
    const mapScript = document.getElementById('product-options-map');
    if (mapScript) {
      try {
        optionsMap = JSON.parse(mapScript.textContent);
      } catch (e) {
        console.error('Error parsing options map:', e);
      }
    }
  }

  function loadRestockDates() {
    const script = document.getElementById('variant-restock-dates');
    if (script) {
      try {
        restockDates = JSON.parse(script.textContent);
      } catch (e) {
        console.error('Error parsing restock dates:', e);
      }
    }
  }

  function loadDeliveryWeeks() {
    var script = document.getElementById('variant-delivery-weeks');
    if (script) {
      try {
        deliveryWeeks = JSON.parse(script.textContent);
      } catch (e) {
        console.error('Error parsing delivery weeks:', e);
      }
    }
  }

  function loadCtoFlags() {
    var script = document.getElementById('variant-cto-flags');
    if (script) {
      try {
        ctoFlags = JSON.parse(script.textContent);
      } catch (e) {
        console.error('Error parsing CTO flags:', e);
      }
    }
  }

  function updateCtoWarning(variant) {
    var warning = document.querySelector('[data-cto-warning]');
    if (!warning) return;
    warning.hidden = !ctoFlags[variant.id];
  }

  // ==========================================
  // VARIANT SELECTOR - PILL BUTTONS
  // ==========================================
  function initVariantSelectors() {
    var pills = document.querySelectorAll('.laptop-variant-pill');

    pills.forEach(function(pill) {
      pill.addEventListener('click', function() {
        var position = parseInt(pill.dataset.optionPosition);
        var value = pill.dataset.optionValue;
        var group = pill.closest('.laptop-variant-group');

        // Nonexistent variant combination — shouldn't happen (hidden), safety net
        if (pill.classList.contains('laptop-variant-pill--nonexistent')) {
          return;
        }

        // Deselect siblings in this group
        group.querySelectorAll('.laptop-variant-pill').forEach(function(p) {
          p.classList.remove('laptop-variant-pill--selected');
        });

        // Select this pill
        pill.classList.add('laptop-variant-pill--selected');

        // Update the header label (use custom display name for color swatches)
        var label = group.querySelector('[data-selected-label]');
        if (label) label.textContent = pill.dataset.displayName || value;

        // Recalculate availability (top-down) and auto-fix lower groups
        updateOptionAvailability();
        autoSelectLowerOptions(position);

        // Find and set the matching variant
        updateSelectedVariant();
      });
    });

    // Run initial availability check
    updateOptionAvailability();
  }

  // ==========================================
  // TOP-DOWN CASCADING AVAILABILITY
  // Each option group only grays out values
  // based on the options ABOVE it (lower position
  // numbers), never based on options below.
  // This way the first group is never grayed out.
  // ==========================================
  function updateOptionAvailability() {
    var groups = document.querySelectorAll('.laptop-variant-group');

    // Collect current selections: { position: value }
    var selections = {};
    groups.forEach(function(group) {
      var pos = parseInt(group.dataset.optionPosition);
      var selected = group.querySelector('.laptop-variant-pill--selected');
      if (selected) selections[pos] = selected.dataset.optionValue;
    });

    // For each group, only check options with a LOWER position number
    groups.forEach(function(group) {
      var pos = parseInt(group.dataset.optionPosition);
      var pills = group.querySelectorAll('.laptop-variant-pill');

      pills.forEach(function(pill) {
        var value = pill.dataset.optionValue;

        // Check if ANY variant exists with this value (given higher options)
        var variantExists = productVariants.some(function(variant) {
          if (variant.options[pos - 1] !== value) return false;
          for (var otherPos in selections) {
            var op = parseInt(otherPos);
            if (op >= pos) continue;
            if (variant.options[op - 1] !== selections[op]) return false;
          }
          return true;
        });

        // Check if any AVAILABLE (in-stock) variant exists
        var isAvailable = variantExists && productVariants.some(function(variant) {
          if (!variant.available) return false;
          if (variant.options[pos - 1] !== value) return false;
          for (var otherPos in selections) {
            var op = parseInt(otherPos);
            if (op >= pos) continue;
            if (variant.options[op - 1] !== selections[op]) return false;
          }
          return true;
        });

        // Three states:
        // 1. Available (in stock) → normal pill
        // 2. Exists but sold out → clickable, sold-out styling
        // 3. Doesn't exist → hidden
        pill.classList.remove('laptop-variant-pill--unavailable');
        pill.classList.toggle('laptop-variant-pill--sold-out', variantExists && !isAvailable);
        pill.classList.toggle('laptop-variant-pill--nonexistent', !variantExists);
      });
    });
  }

  // After changing an option, auto-select the first available
  // pill in any lower group whose current selection became unavailable.
  function autoSelectLowerOptions(changedPosition) {
    var groups = Array.from(document.querySelectorAll('.laptop-variant-group'));
    // Sort by position to process in order
    groups.sort(function(a, b) {
      return parseInt(a.dataset.optionPosition) - parseInt(b.dataset.optionPosition);
    });

    groups.forEach(function(group) {
      var pos = parseInt(group.dataset.optionPosition);
      if (pos <= changedPosition) return;

      var selectedPill = group.querySelector('.laptop-variant-pill--selected');

      // If current selection still exists (available or sold-out), keep it
      if (selectedPill && !selectedPill.classList.contains('laptop-variant-pill--nonexistent')) return;

      // Otherwise, select first available pill; fall back to first sold-out pill
      var pills = group.querySelectorAll('.laptop-variant-pill');
      pills.forEach(function(p) { p.classList.remove('laptop-variant-pill--selected'); });

      var fallback = null;
      for (var i = 0; i < pills.length; i++) {
        if (pills[i].classList.contains('laptop-variant-pill--nonexistent')) continue;
        if (!pills[i].classList.contains('laptop-variant-pill--sold-out')) {
          // Available — pick this one
          fallback = pills[i];
          break;
        }
        if (!fallback) fallback = pills[i]; // First sold-out as fallback
      }
      if (fallback) {
        fallback.classList.add('laptop-variant-pill--selected');
        var label = group.querySelector('[data-selected-label]');
        if (label) label.textContent = fallback.dataset.displayName || fallback.dataset.optionValue;
      }

      // Re-run availability for groups below this one since selection changed
      updateOptionAvailability();
    });
  }

  // When a greyed-out pill is clicked, find the closest available variant
  // that includes that option value (changing as few other options as possible).
  function selectClosestVariant(position, value) {
    var candidates = productVariants.filter(function(v) {
      return v.available && v.options[position - 1] === value;
    });

    if (candidates.length === 0) return;

    // Get current selections
    var currentSelections = [];
    document.querySelectorAll('.laptop-variant-group').forEach(function(group) {
      var pos = parseInt(group.dataset.optionPosition);
      var selected = group.querySelector('.laptop-variant-pill--selected');
      if (selected) currentSelections[pos - 1] = selected.dataset.optionValue;
    });

    // Pick the candidate closest to current selections (most options in common)
    var best = candidates[0];
    var bestScore = -1;
    candidates.forEach(function(variant) {
      var score = 0;
      variant.options.forEach(function(opt, i) {
        if (i === position - 1) return; // skip the clicked option
        if (currentSelections[i] === opt) score++;
      });
      if (score > bestScore) {
        bestScore = score;
        best = variant;
      }
    });

    // Update all pill groups to match the chosen variant
    document.querySelectorAll('.laptop-variant-group').forEach(function(group) {
      var pos = parseInt(group.dataset.optionPosition);
      var targetValue = best.options[pos - 1];
      var pills = group.querySelectorAll('.laptop-variant-pill');

      pills.forEach(function(p) {
        p.classList.remove('laptop-variant-pill--selected');
      });
      pills.forEach(function(p) {
        if (p.dataset.optionValue === targetValue) {
          p.classList.add('laptop-variant-pill--selected');
          var label = group.querySelector('[data-selected-label]');
          if (label) label.textContent = p.dataset.displayName || targetValue;
        }
      });
    });

    updateOptionAvailability();
    updateSelectedVariant();
  }

  function updateSelectedVariant() {
    // Get all selected option values from pill buttons
    const selectedOptions = [];
    document.querySelectorAll('.laptop-variant-group').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition);
      const selected = group.querySelector('.laptop-variant-pill--selected');
      if (selected && pos) {
        selectedOptions[pos - 1] = selected.dataset.optionValue;
      }
    });

    // Find matching variant (skip positions for hidden single-value option groups)
    const matchingVariant = productVariants.find(variant => {
      return variant.options.every((opt, i) => selectedOptions[i] === undefined || selectedOptions[i] === opt);
    });

    if (matchingVariant) {
      currentVariant = matchingVariant;

      // Update hidden variant ID input
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = matchingVariant.id;
      }

      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', matchingVariant.id);
      history.replaceState({}, '', url.toString());

      // Update gallery image to variant image
      updateGalleryForVariant(matchingVariant);

      // Update price display
      updatePriceDisplay(matchingVariant);

      // Update add to cart button
      updateAddToCartButton(matchingVariant);

      // Update SKU display
      updateSku(matchingVariant);

      // Update auto-badges
      updateAutoBadges(matchingVariant);
      updateGeizhalsComparison(matchingVariant);
      updateAvailability(matchingVariant);
      updateLowStock(matchingVariant);
      updateDeliveryNotice(matchingVariant);
      updateCtoWarning(matchingVariant);
    } else {
      // No matching variant exists — disable add to cart
      const button = document.querySelector('.laptop-add-to-cart__button');
      const buttonText = document.querySelector('[data-add-to-cart-text]');
      if (button) {
        button.disabled = true;
        if (buttonText) buttonText.textContent = button.dataset.soldOutText || 'derzeit Ausverkauft';
      }
    }
  }

  function updateSku(variant) {
    var container = document.querySelector('[data-variant-sku]');
    if (!container) return;
    var skuValue = container.querySelector('[data-sku-value]');
    if (skuValue) skuValue.textContent = variant.sku || '';
    container.hidden = !variant.sku;
  }

  function updatePriceDisplay(variant) {
    const priceEl = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-compare-price]');
    const savingsEl = document.querySelector('[data-price-savings]');

    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareEl) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      }
      if (savingsEl) {
        const savings = variant.compare_at_price - variant.price;
        savingsEl.textContent = 'Save ' + formatMoney(savings);
        savingsEl.style.display = '';
      }
    } else {
      if (compareEl) compareEl.style.display = 'none';
      if (savingsEl) savingsEl.style.display = 'none';
    }
  }

  function updateAddToCartButton(variant) {
    const button = document.querySelector('.laptop-add-to-cart__button');
    const buttonText = document.querySelector('[data-add-to-cart-text]');

    if (!button) return;

    // Read the configurable texts from data attributes
    const addText = button.dataset.addText || 'Jetzt bestellen';
    const soldOutText = button.dataset.soldOutText || 'derzeit Ausverkauft';

    var restockMsg = document.querySelector('[data-restock-message]');
    var restockDateEl = document.querySelector('[data-restock-date]');
    var variantDate = restockDates[variant.id];

    if (variant.available) {
      button.disabled = false;
      if (buttonText) {
        buttonText.textContent = addText;
      }
      if (restockMsg) restockMsg.hidden = true;
    } else {
      button.disabled = true;
      if (buttonText) {
        buttonText.textContent = soldOutText;
      }
      if (restockMsg && variantDate) {
        if (restockDateEl) restockDateEl.textContent = variantDate;
        restockMsg.hidden = false;
      } else if (restockMsg) {
        restockMsg.hidden = true;
      }
    }
  }

  // ==========================================
  // GEIZHALS COMPARISON
  // ==========================================
  function loadGeizhalsData() {
    var script = document.getElementById('variant-geizhals-data');
    if (script) {
      try {
        geizhalsData = JSON.parse(script.textContent);
      } catch (e) {
        console.error('Error parsing Geizhals data:', e);
      }
    }
  }

  function updateGeizhalsComparison(variant) {
    var container = document.querySelector('[data-geizhals-comparison]');
    if (!container || !geizhalsData) return;

    var data = geizhalsData[variant.id];

    if (data && data.price > 0 && variant.price < data.price) {
      var gzPriceEl = container.querySelector('[data-geizhals-gz-price]');
      var linkTextEl = container.querySelector('[data-geizhals-link-text]');

      if (gzPriceEl) {
        gzPriceEl.textContent = formatMoneyPrefix(data.price);
        gzPriceEl.href = data.url;
      }
      if (linkTextEl) linkTextEl.href = data.url;

      container.removeAttribute('hidden');
    } else {
      container.setAttribute('hidden', '');
    }

    // Update the savings badge on the product image
    var badge = document.querySelector('[data-geizhals-badge]');
    if (badge) {
      if (data && data.price > 0 && variant.price < data.price) {
        var savings = data.price - variant.price;
        badge.textContent = '- ' + formatMoneyPrefix(savings) + ' zu Geizhals-Preis';
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    }
  }

  function loadAvailabilityData() {
    var script = document.getElementById('variant-availability-data');
    if (script) {
      try {
        availabilityData = JSON.parse(script.textContent);
      } catch (e) {
        console.error('Error parsing availability data:', e);
      }
    }
  }

  function loadInventoryData() {
    var script = document.getElementById('variant-inventory-data');
    if (script) {
      try {
        inventoryData = JSON.parse(script.textContent);
      } catch (e) {
        console.error('Error parsing inventory data:', e);
      }
    }
  }

  function updateLowStock(variant) {
    if (!inventoryData) return;

    var info = inventoryData[variant.id];

    // Update the text indicator (below details)
    var container = document.querySelector('[data-low-stock]');
    if (container) {
      var threshold = parseInt(container.dataset.threshold, 10) || 5;
      var lowText = container.dataset.lowText || '';
      var outText = container.dataset.outText || '';
      var dot = container.querySelector('.laptop-low-stock__dot');
      var text = container.querySelector('[data-low-stock-text]');

      if (!info || !info.managed) {
        container.hidden = true;
      } else if (info.quantity > 0 && info.quantity <= threshold) {
        container.hidden = false;
        container.classList.remove('laptop-low-stock--out');
        if (dot) dot.className = 'laptop-low-stock__dot laptop-low-stock__dot--warning';
        if (text) text.textContent = lowText.replace('[count]', info.quantity);
      } else if (info.quantity <= 0 && info.policy === 'deny') {
        container.hidden = false;
        container.classList.add('laptop-low-stock--out');
        if (dot) dot.className = 'laptop-low-stock__dot laptop-low-stock__dot--danger';
        if (text) text.textContent = outText;
      } else {
        container.hidden = true;
      }
    }

    // Update the image badge overlay
    var badge = document.querySelector('[data-low-stock-badge]');
    if (badge) {
      var badgeText = badge.dataset.badgeText || '';
      var badgeTextEl = badge.querySelector('[data-low-stock-badge-text]');
      var badgeThreshold = container ? (parseInt(container.dataset.threshold, 10) || 5) : 5;

      if (info && info.managed && info.quantity > 0 && info.quantity <= badgeThreshold) {
        badge.hidden = false;
        if (badgeTextEl) badgeTextEl.textContent = badgeText.replace('[count]', info.quantity);
      } else {
        badge.hidden = true;
      }
    }
  }

  function updateDeliveryNotice(variant) {
    var notice = document.querySelector('[data-delivery-notice]');
    if (!notice) return;

    var weeks = deliveryWeeks[variant.id];
    if (weeks) {
      var template = notice.dataset.deliveryTemplate || 'erwartete Lieferzeit [weeks] Wochen';
      notice.textContent = template.replace('[weeks]', weeks);
      notice.hidden = false;
    } else {
      notice.hidden = true;
    }
  }

  function updateAvailability(variant) {
    var container = document.querySelector('[data-availability]');
    if (!container || !availabilityData) return;

    var status = availabilityData[variant.id] || 'unavailable';
    var dot = container.querySelector('.laptop-availability__dot');
    var text = container.querySelector('.laptop-availability__text');
    if (!dot || !text) return;

    dot.className = 'laptop-availability__dot';

    // Lead time takes precedence for orderable variants: if this variant has a
    // delivery-weeks value, show "Lieferzeit X Wochen" with an amber dot instead
    // of the "sofort" texts. Unavailable variants always fall through to red.
    var weeks = deliveryWeeks[variant.id];
    if (weeks && status !== 'unavailable') {
      var leadtimeTemplate = container.dataset.leadtimeTemplate || 'Lieferzeit [weeks] Wochen';
      dot.classList.add('laptop-availability__dot--amber');
      text.textContent = leadtimeTemplate.replace('[weeks]', weeks);
      container.hidden = false;
      return;
    }

    // "Neu" products only show the lead-time state
    if (container.hasAttribute('data-leadtime-only')) {
      container.hidden = true;
      return;
    }
    container.hidden = false;

    // Read texts from the container's data or from the existing DOM
    var storeText = container.dataset.storeText;
    var deliveryText = container.dataset.deliveryText;
    var unavailableText = container.dataset.unavailableText;

    if (status === 'store') {
      dot.classList.add('laptop-availability__dot--green');
      text.textContent = storeText;
    } else if (status === 'delivery') {
      dot.classList.add('laptop-availability__dot--green');
      text.textContent = deliveryText;
    } else {
      dot.classList.add('laptop-availability__dot--red');
      text.textContent = unavailableText;
    }
  }

  function formatMoney(cents) {
    // Get currency from Shopify or default to EUR
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'de-DE';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  }

  function formatMoneyPrefix(cents) {
    // Format as €X,XX (symbol before number)
    var value = (cents / 100).toFixed(2).replace('.', ',');
    return '€' + value;
  }

  // ==========================================
  // AUTO-BADGES UPDATE (variant-aware)
  // Budget: depends on variant price
  // Gesamtperformance: CPU + RAM evaluated from variant options
  // ==========================================
  function updateAutoBadges(variant) {
    // Budget badge: variant price < 800€
    var budgetBadge = document.querySelector('[data-badge="budget"]');
    if (budgetBadge) {
      budgetBadge.hidden = !(variant.price < 80000);
    }

    // Gesamtperformance badge
    var perfBadge = document.querySelector('[data-badge="performance"]');
    if (perfBadge) {
      var cpuDynamic = perfBadge.dataset.cpuDynamic === 'true';
      var ramDynamic = perfBadge.dataset.ramDynamic === 'true';

      // If neither is dynamic, Liquid handles it — skip
      if (!cpuDynamic && !ramDynamic) return;

      // CPU evaluation
      var hasGoodCpu = false;
      if (cpuDynamic) {
        var cpuPos = optionsMap['CPU'];
        if (cpuPos) {
          var cpu = (variant.options[cpuPos - 1] || '').toLowerCase();
          if (cpu.indexOf('i7') !== -1 || cpu.indexOf('i9') !== -1
            || cpu.indexOf('ultra 7') !== -1 || cpu.indexOf('ultra 9') !== -1
            || cpu.indexOf('ultra7') !== -1 || cpu.indexOf('ultra9') !== -1
            || cpu.indexOf('ryzen 7') !== -1 || cpu.indexOf('ryzen 9') !== -1
            || cpu.indexOf('ryzen7') !== -1 || cpu.indexOf('ryzen9') !== -1
            || cpu.indexOf('m2') !== -1 || cpu.indexOf('m3') !== -1
            || cpu.indexOf('m4') !== -1 || cpu.indexOf('m5') !== -1
            || cpu.indexOf('m6') !== -1) {
            hasGoodCpu = true;
          }
        }
      } else {
        hasGoodCpu = perfBadge.dataset.goodCpu === 'true';
      }

      // RAM evaluation
      var hasGoodRam = false;
      if (ramDynamic) {
        var ramPos = optionsMap['RAM'];
        if (ramPos) {
          var ram = (variant.options[ramPos - 1] || '').toLowerCase();
          var ramNum = parseInt(ram.replace(/gb/gi, '').replace(/\s/g, ''), 10);
          if (!isNaN(ramNum) && ramNum >= 16) {
            hasGoodRam = true;
          }
        }
      } else {
        // RAM is static (no variant option) — if badge is rendered, Liquid already confirmed good RAM
        hasGoodRam = true;
      }

      perfBadge.hidden = !(hasGoodCpu && hasGoodRam);
    }
  }

  // ==========================================
  // IMAGE GALLERY
  // ==========================================
  function updateGalleryForVariant(variant) {
    if (!variant.featured_image) return;

    var mainImage = document.querySelector('.laptop-gallery__main-image');
    if (!mainImage) return;

    // Build the 800w URL from the variant's featured image src
    var imgSrc = variant.featured_image.src;
    // Shopify image URLs can be resized by inserting _800x before the extension
    // But variant.featured_image.src is already full-size, so use it directly
    mainImage.src = imgSrc;
    mainImage.alt = variant.featured_image.alt || '';

    // Highlight matching thumbnail if one exists
    var variantFile = imgSrc.split('?')[0].split('/').pop();
    var thumbnails = document.querySelectorAll('.laptop-gallery__thumbnail');
    var matched = false;
    thumbnails.forEach(function(thumb) {
      thumb.classList.remove('laptop-gallery__thumbnail--active');
      if (!matched && thumb.dataset.fullSrc) {
        var thumbFile = thumb.dataset.fullSrc.split('?')[0].split('/').pop();
        if (thumbFile === variantFile) {
          thumb.classList.add('laptop-gallery__thumbnail--active');
          matched = true;
        }
      }
    });
  }

  function initImageGallery() {
    const mainImage = document.querySelector('.laptop-gallery__main-image');
    const thumbnails = document.querySelectorAll('.laptop-gallery__thumbnail');

    if (!mainImage || thumbnails.length === 0) return;

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        // Update main image
        const newSrc = thumb.dataset.fullSrc;
        if (newSrc) {
          mainImage.src = newSrc;
          mainImage.alt = thumb.querySelector('img')?.alt || '';
        }

        // Update active state
        thumbnails.forEach(t => t.classList.remove('laptop-gallery__thumbnail--active'));
        thumb.classList.add('laptop-gallery__thumbnail--active');
      });
    });
  }

  // ==========================================
  // SHARE BUTTON
  // ==========================================
  function initShareButton() {
    const shareBtn = document.querySelector('.laptop-share-button');
    if (!shareBtn) return;

    shareBtn.addEventListener('click', async () => {
      const shareData = {
        title: document.title,
        url: window.location.href
      };

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
        } catch (err) {
          // User cancelled or error - fail silently
          if (err.name !== 'AbortError') {
            fallbackShare();
          }
        }
      } else {
        fallbackShare();
      }
    });
  }

  function fallbackShare() {
    // Copy URL to clipboard
    navigator.clipboard.writeText(window.location.href).then(() => {
      // Show brief feedback
      const shareBtn = document.querySelector('.laptop-share-button');
      if (shareBtn) {
        const originalHTML = shareBtn.innerHTML;
        shareBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
        setTimeout(() => {
          shareBtn.innerHTML = originalHTML;
        }, 2000);
      }
    }).catch(() => {
      // Clipboard API not available - fail silently
    });
  }

  // ==========================================
  // UPSELL ADD-ONS
  // ==========================================
  function initUpsellForm() {
    const form = document.querySelector('.laptop-add-to-cart-form');
    if (!form) return;

    const upsellContainer = document.querySelector('[data-upsells]');
    if (!upsellContainer) return;

    // --- Radio deselect: clicking an already-selected garantie radio unchecks it ---
    upsellContainer.querySelectorAll('[data-upsell-radio]').forEach(function(radio) {
      radio.addEventListener('click', function(e) {
        if (radio._wasChecked) {
          radio.checked = false;
          radio._wasChecked = false;
        } else {
          // Unmark all radios in the same name group, then mark this one
          upsellContainer.querySelectorAll('[data-upsell-radio]').forEach(function(r) {
            r._wasChecked = false;
          });
          radio._wasChecked = true;
        }
      });
      // Track state on mousedown (before the click toggles the radio)
      radio.addEventListener('mousedown', function() {
        radio._wasChecked = radio.checked;
      });
    });

    // --- Title links: stop propagation so clicking title navigates instead of toggling ---
    upsellContainer.querySelectorAll('[data-upsell-link]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    });

    // --- Cart submission: collect radios + checkboxes ---
    form.addEventListener('submit', function(e) {
      const checkedCheckboxes = upsellContainer.querySelectorAll('[data-upsell-checkbox]:checked');
      const checkedRadios = upsellContainer.querySelectorAll('[data-upsell-radio]:checked');
      const totalSelected = checkedCheckboxes.length + checkedRadios.length;

      if (totalSelected === 0) return; // No upsells selected, let form submit normally

      e.preventDefault();

      const button = form.querySelector('.laptop-add-to-cart__button');
      const buttonText = form.querySelector('[data-add-to-cart-text]');
      if (button) button.disabled = true;

      // Build items array: main product + selected upsells
      const variantInput = form.querySelector('input[name="id"]');
      const items = [{ id: parseInt(variantInput.value), quantity: 1 }];

      checkedRadios.forEach(function(radio) {
        items.push({ id: parseInt(radio.value), quantity: 1 });
      });
      checkedCheckboxes.forEach(function(checkbox) {
        items.push({ id: parseInt(checkbox.value), quantity: 1 });
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function(response) {
          if (!response.ok) throw new Error('Cart add failed');
          return response.json();
        })
        .then(function() {
          // Update cart count in header
          updateCartBubble();

          // Show success feedback on button
          if (buttonText) {
            var originalText = buttonText.textContent;
            buttonText.textContent = '\u2713';
            setTimeout(function() {
              buttonText.textContent = originalText;
              if (button) button.disabled = false;
            }, 1500);
          } else {
            if (button) button.disabled = false;
          }

          // Open cart drawer if that's the cart type, otherwise redirect
          if (window.Shopify && window.Shopify.routes && window.location.pathname !== '/cart') {
            var cartType = document.documentElement.dataset.cartType ||
                           document.querySelector('[data-cart-type]')?.dataset.cartType;
            if (cartType === 'drawer') {
              // Dispatch event for cart drawer to open
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            } else {
              window.location.href = window.Shopify.routes.root + 'cart';
            }
          }
        })
        .catch(function(error) {
          console.error('Error adding items to cart:', error);
          if (button) button.disabled = false;
          if (buttonText) buttonText.textContent = button.dataset.addText || 'Jetzt bestellen';
        });
    });
  }

  function updateCartBubble() {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Update all cart count bubbles in the header
        document.querySelectorAll('[data-cart-count], .cart-count-bubble span, .cart-bubble__count').forEach(function(el) {
          el.textContent = cart.item_count;
        });
        // Show bubble if it was hidden
        document.querySelectorAll('.cart-count-bubble, .cart-bubble').forEach(function(el) {
          if (cart.item_count > 0) {
            el.removeAttribute('hidden');
            el.style.display = '';
          }
        });
      })
      .catch(function() {});
  }

  // ==========================================
  // INITIALIZE ON DOM READY
  // ==========================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
